import type { FC } from 'react';

import classNames from 'classnames';

import VARIANTS from '../../constants/variants';
import { refreshAppState } from '../../ducks/app/slice';
import { selectSearchItemByOffset } from '../../ducks/navigation_history/selectors';
import {
  navigationHistoryGoNext,
  navigationHistoryGoPrev,
  upToPrevNavigationItem,
} from '../../ducks/navigation_history/thunks';
import { useAppDispatch, useAppSelector } from '../../store/react_redux_typed';
import Button from '../../ui/button/button';
import TitleNavigationPreview from '../title_navigation_preview/title_navigation_preview';
import TooltipTrigger from '../tooltip_trigger/tooltip_trigger';

import styles from './title_navigation.module.css';

type Props = {
  className?: string;
  canNavigateUp?: boolean;
  canRefresh?: boolean;
  currentEntityId?: string;
};

const TitleNavigation: FC<Props> = (props) => {
  const prevSearchItem = useAppSelector((state) =>
    selectSearchItemByOffset(state, props.currentEntityId, -1),
  );
  const nextSearchItem = useAppSelector((state) =>
    selectSearchItemByOffset(state, props.currentEntityId, 1),
  );
  const prevNavigationHistoryItem = useAppSelector((state) =>
    state.navigationHistory.history.at(-2),
  );

  const dispatch = useAppDispatch();

  const handleRefreshClick = () => dispatch(refreshAppState());

  const handleUpClick = () => {
    dispatch(upToPrevNavigationItem());
  };

  const handlePrevClick = () => {
    dispatch(navigationHistoryGoPrev(props.currentEntityId));
  };

  const handleNextClick = () => {
    dispatch(navigationHistoryGoNext(props.currentEntityId));
  };

  return (
    <div className={classNames(props.className, styles.wrapper)}>
      {prevNavigationHistoryItem ? (
        <Button
          wideBasis={false}
          noBorder
          iconName="chevron_large_isolated_n"
          className={styles.btn}
          onClick={handleUpClick}
          variant="white"
          aria={{
            label: 'Navigate up',
          }}
          uiKey="title-navigation-up"
        />
      ) : null}
      {prevSearchItem || nextSearchItem ? (
        <>
          <TooltipTrigger
            forcePlacement
            tooltipText={<TitleNavigationPreview searchItem={prevSearchItem} />}
            tooltipClass={styles.tooltip}
            placement="bottom"
            component={Button}
            shouldShowTooltip={() => Boolean(prevSearchItem)}
            componentProps={{
              className: styles.btn,
              onClick: handlePrevClick,
              disabled: !prevSearchItem,
              iconName: 'chevron_large_isolated_w',
              noBorder: true,
              wideBasis: false,
              variant: VARIANTS.WHITE,
              uiKey: 'title-navigation-prev',
              aria: {
                label: 'Navigate to previous',
              },
            }}
          />
          <TooltipTrigger
            forcePlacement
            tooltipText={<TitleNavigationPreview searchItem={nextSearchItem} />}
            tooltipClass={styles.tooltip}
            placement="bottom"
            component={Button}
            shouldShowTooltip={() => Boolean(nextSearchItem)}
            componentProps={{
              className: styles.btn,
              onClick: handleNextClick,
              disabled: !nextSearchItem,
              iconName: 'chevron_large_isolated_e',
              noBorder: true,
              wideBasis: false,
              variant: 'white',
              uiKey: 'title-navigation-next',
              aria: {
                label: 'Navigate to next',
              },
            }}
          />
        </>
      ) : undefined}
      {props.canRefresh ? (
        <Button
          className={styles.btn}
          onClick={handleRefreshClick}
          iconName="refresh"
          noBorder
          wideBasis={false}
          variant="white"
          uiKey="title-navigation-refresh"
          aria={{
            label: 'Refresh',
          }}
        />
      ) : null}
    </div>
  );
};

export default TitleNavigation;
